// ===============================================================================
// BusinessExpenseAnalysis.js - Business Expense & Income Analysis Page for SplitMon
// ===============================================================================
// Location: /var/www/wow/frontend/src/BusinessExpenseAnalysis.js
// Premium Feature: Comprehensive analytics for business expense groups
// UPDATED: Added Profit/Loss tab, number formatting, current balance
// ===============================================================================

import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, ComposedChart, ReferenceLine, Area
} from "recharts";
import { businessAnalysisAPI, formatCurrency } from "./api";
import "./BusinessExpenseAnalysis.css";

// Chart colors - Forest & Cream theme (matching ExpenseAnalysis)
const CHART_COLORS = [
  "#2D5A3D", // Forest (primary)
  "#4A7C59", // Forest light
  "#D4A574", // Tan
  "#8FBC8F", // Dark Sea Green
  "#6B8E6B", // Sage
  "#C4A77D", // Tan light
  "#3D6B4F", // Forest medium
  "#A0C49D", // Light green
  "#E8D4B8", // Cream dark
];

// Profit/Loss specific colors
const PROFIT_COLOR = "#2D5A3D";  // Green for profit/income
const LOSS_COLOR = "#dc2626";    // Red for loss/expenses
const NEUTRAL_COLOR = "#6b7280"; // Gray for neutral

// ===============================================================================
// Number Formatting Helper - Format with commas (e.g., 1,351.67)
// ===============================================================================
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '0.00';
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Format currency with symbol and commas
const formatAmount = (amount, currency = 'EUR') => {
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency === 'GBP' ? '£' : currency;
  return `${symbol}${formatNumber(amount)}`;
};

// Helper to extract error message
const getErrorMessage = (err, defaultMessage) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object' && detail.msg) return detail.msg;
  }
  return err?.message || defaultMessage;
};

// Format date for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ===============================================================================
// Date Range Presets
// ===============================================================================
const DATE_RANGE_PRESETS = [
  { id: 'project_duration', label: 'Total Project Duration' },
  { id: 'current_year', label: 'Current Year' },
  { id: 'this_month', label: 'Current Month' },
  { id: 'last_12_months', label: 'Last 12 Months' },
  { id: 'last_6_months', label: 'Last 6 Months' },
  { id: 'last_3_months', label: 'Last 3 Months' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'custom', label: 'Custom Range' }, // shown only when user edits dates manually
];

const DEFAULT_PRESET = 'project_duration';

const toISODate = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Compute { dateFrom, dateTo } for a given preset id. Returns null for 'custom'.
const getPresetDateRange = (presetId) => {
  const today = new Date();

  switch (presetId) {
    case 'current_year':
      return { dateFrom: toISODate(new Date(today.getFullYear(), 0, 1)), dateTo: toISODate(today) };
     case 'project_duration':
      return { dateFrom: '1901-01-01', dateTo: toISODate(today) };
    case 'last_6_months':
      return { dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth() - 6, today.getDate())), dateTo: toISODate(today) };
    case 'last_12_months':
      return { dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth() - 12, today.getDate())), dateTo: toISODate(today) };
    case 'last_3_months':
      return { dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth() - 3, today.getDate())), dateTo: toISODate(today) };
    case 'last_month':
      return {
        dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        dateTo: toISODate(new Date(today.getFullYear(), today.getMonth(), 0)) // day 0 = last day of previous month
      };
    case 'this_month':
      return { dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth(), 1)), dateTo: toISODate(today) };
    default:
      return null; // 'custom'
  }
};

function BusinessExpenseAnalysis({ user, onBack }) {
  // ===============================================================================
  // State
  // ===============================================================================
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Date filters
  const defaultDates = getPresetDateRange(DEFAULT_PRESET);
  const [dateFrom, setDateFrom] = useState(defaultDates.dateFrom);
  const [dateTo, setDateTo] = useState(defaultDates.dateTo);
  const [dateRangePreset, setDateRangePreset] = useState(DEFAULT_PRESET); 
  
  // Group filter
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [showGroupFilter, setShowGroupFilter] = useState(false);
  
  // Data states
  const [summaryData, setSummaryData] = useState(null);
  const [perPersonData, setPerPersonData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [profitLossData, setProfitLossData] = useState(null);
  
  // Expanded sections
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedPersons, setExpandedPersons] = useState({});
  const [expandedPLGroups, setExpandedPLGroups] = useState({});

  // ===============================================================================
  // Load Available Groups on Mount
  // ===============================================================================
  useEffect(() => {
    loadAvailableGroups();
  }, []);

  const loadAvailableGroups = async () => {
    try {
      const res = await businessAnalysisAPI.getGroups();
      if (res.data?.ok) {
        setAvailableGroups(res.data.groups || []);
      }
    } catch (err) {
      console.error("Load groups error:", err);
    }
  };

  // ===============================================================================
  // Load Data on Mount and Filter Change
  // ===============================================================================
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadAllData();
  }, []);

  // Core fetch — takes explicit filters so it can be called with fresh values
  // immediately after a preset change, without waiting on React state to flush.
  const fetchAnalysisData = useCallback(async (filters) => {
    setLoading(true);
    setError("");

    try {
      const [summaryRes, perPersonRes, trendsRes, insightsRes, profitLossRes] = await Promise.all([
        businessAnalysisAPI.getSummary(filters),
        businessAnalysisAPI.getPerPerson(filters),
        businessAnalysisAPI.getTrends(filters),
        businessAnalysisAPI.getInsights(filters),
        businessAnalysisAPI.getProfitLoss(filters)
      ]);

      if (summaryRes.data?.ok) setSummaryData(summaryRes.data);
      if (perPersonRes.data?.ok) setPerPersonData(perPersonRes.data);
      if (trendsRes.data?.ok) setTrendsData(trendsRes.data);
      if (insightsRes.data?.ok) setInsightsData(insightsRes.data);
      if (profitLossRes.data?.ok) setProfitLossData(profitLossRes.data);

    } catch (err) {
      console.error("Load analysis error:", err);
      setError(getErrorMessage(err, "Failed to load analysis data"));
    }

    setLoading(false);
  }, []);

  // Wrapper that reads current dateFrom/dateTo/selectedGroupIds state —
  // used on mount and by the "Apply Filters" button (manual edits).
  const loadAllData = useCallback(() => {
    return fetchAnalysisData({
      date_from: dateFrom,
      date_to: dateTo,
      group_ids: selectedGroupIds.length > 0 ? selectedGroupIds : null
    });
  }, [dateFrom, dateTo, selectedGroupIds, fetchAnalysisData]);

  const handleApplyFilters = () => {
    loadAllData();
    setShowGroupFilter(false);
  };

  // Preset dropdown handler — computes dates and fetches immediately,
  // rather than waiting for state + the Apply button.
  const handlePresetChange = (e) => {
    const presetId = e.target.value;
    setDateRangePreset(presetId);

    if (presetId === 'custom') return; // no-op; user is driving dates manually

    const range = getPresetDateRange(presetId);
    if (!range) return;

    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);

    fetchAnalysisData({
      date_from: range.dateFrom,
      date_to: range.dateTo,
      group_ids: selectedGroupIds.length > 0 ? selectedGroupIds : null
    });
  };

  // If the user edits a date field by hand, drop into "Custom Range" mode
  // so the dropdown accurately reflects that the dates no longer match a preset.
  const handleManualDateChange = (field, value) => {
    if (field === 'from') setDateFrom(value);
    else setDateTo(value);
    setDateRangePreset('custom');
  };

  // ===============================================================================
  // Group Filter Handlers
  // ===============================================================================
  const toggleGroupSelection = (groupId) => {
    setSelectedGroupIds(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const selectAllGroups = () => {
    setSelectedGroupIds(availableGroups.map(g => g.id));
  };

  const clearAllGroups = () => {
    setSelectedGroupIds([]);
  };

  // ===============================================================================
  // Toggle Handlers
  // ===============================================================================
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const toggleType = (typeId) => {
    setExpandedTypes(prev => ({ ...prev, [typeId]: !prev[typeId] }));
  };

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePerson = (userId) => {
    setExpandedPersons(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const togglePLGroup = (groupId) => {
    setExpandedPLGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // ===============================================================================
  // Calculate Profit/Loss Percentage
  // ===============================================================================
  const getProfitLossPercentage = () => {
    if (!profitLossData || !profitLossData.total_income || profitLossData.total_income === 0) {
      return null;
    }
    const percentage = ((profitLossData.net_profit_loss / profitLossData.total_income) * 100);
    return percentage;
  };

  // ===============================================================================
  // Custom Tooltips for Charts
  // ===============================================================================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="biz-chart-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-value" style={{ color: entry.color }}>
              {entry.name}: €{formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="biz-chart-tooltip">
          <p className="tooltip-label">{data.category_icon || data.source_icon} {data.category_name || data.source_label || data.type_name}</p>
          <p className="tooltip-value">€{formatNumber(data.total_amount)}</p>
          <p className="tooltip-percent">{data.percentage}%</p>
        </div>
      );
    }
    return null;
  };

  const ProfitLossTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="biz-chart-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-value" style={{ color: entry.color }}>
              {entry.name}: €{formatNumber(Math.abs(entry.value))}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // ===============================================================================
  // Render Loading
  // ===============================================================================
  if (loading) {
    return (
      <div className="biz-analysis-page">
        <div className="biz-loading">
          <div className="biz-spinner"></div>
          <p>Loading business analysis...</p>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Render Error State
  // ===============================================================================
  if (error && !summaryData) {
    return (
      <div className="biz-analysis-page">
        <div className="biz-analysis-header">
          <div className="biz-header-content">
            <div className="biz-header-title">
              <h1>📊 Business Analysis</h1>
              <span className="biz-premium-badge">⭐ Premium</span>
            </div>
          </div>
          <button className="biz-back-btn" onClick={onBack}>← Back</button>
        </div>
        <div className="biz-error-card">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load</h3>
          <p>{error}</p>
          <button className="biz-btn-primary" onClick={loadAllData}>Try Again</button>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Render Overview Tab
  // ===============================================================================
  const renderOverviewTab = () => {
    const plPercentage = getProfitLossPercentage();
    
    return (
      <div className="biz-tab-content">
        {/* Quick Stats */}
        <div className="biz-stats-grid">
          <div className="biz-stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <span className="stat-value">€{formatNumber(insightsData?.total_expenses)}</span>
              <span className="stat-label">Total Expenses</span>
            </div>
          </div>
          <div className="biz-stat-card">
            <div className="stat-icon">📥</div>
            <div className="stat-content">
              <span className="stat-value income-value">€{formatNumber(profitLossData?.total_income)}</span>
              <span className="stat-label">Total Income</span>
            </div>
          </div>
          <div className="biz-stat-card">
            <div className="stat-icon">{(profitLossData?.net_profit_loss || 0) >= 0 ? '📈' : '📉'}</div>
            <div className="stat-content">
              <span className={`stat-value ${(profitLossData?.net_profit_loss || 0) >= 0 ? 'profit-value' : 'loss-value'}`}>
                {(profitLossData?.net_profit_loss || 0) >= 0 ? '+' : ''}€{formatNumber(profitLossData?.net_profit_loss)}
              </span>
              <span className="stat-label">Net Profit/Loss</span>
            </div>
          </div>
          <div className="biz-stat-card">
            <div className="stat-icon">🏢</div>
            <div className="stat-content">
              <span className="stat-value">{insightsData?.groups_analyzed || 0}</span>
              <span className="stat-label">Groups Analyzed</span>
            </div>
          </div>
        </div>

        {/* Your Summary - Compact with Percentage */}
        <div className="biz-your-summary compact">
          <h3>Your Business Summary</h3>
          <div className="summary-row">
            <div className="summary-item">
              <span className="summary-label">Income Share</span>
              <span className="summary-value income-value">€{formatNumber(profitLossData?.your_total_income)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Expense Share</span>
              <span className="summary-value expense-value">€{formatNumber(profitLossData?.your_total_expenses)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Your Net</span>
              <span className={`summary-value ${(profitLossData?.your_net || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(profitLossData?.your_net || 0) >= 0 ? '+' : ''}€{formatNumber(profitLossData?.your_net)}
              </span>
            </div>
            {plPercentage !== null && (
              <div className="summary-item percentage-item">
                <span className="summary-label">Profit Margin</span>
                <span className={`summary-value percentage ${plPercentage >= 0 ? 'positive' : 'negative'}`}>
                  {plPercentage >= 0 ? '+' : ''}{formatNumber(plPercentage, 1)}%
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Charts Row */}
        <div className="biz-charts-row">
          {/* Category Pie Chart */}
          <div className="biz-chart-card">
            <h3>Expenses by Category</h3>
            {trendsData?.category_totals?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={trendsData.category_totals.slice(0, 8)}
                      dataKey="total_amount"
                      nameKey="category_name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                    >
                      {trendsData.category_totals.slice(0, 8).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Category Legend */}
                <div className="biz-pie-legend">
                  {trendsData.category_totals.slice(0, 5).map((cat, index) => (
                    <div key={cat.category_code} className="legend-item">
                      <span className="legend-color" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                      <span className="legend-label">{cat.category_icon} {cat.category_name}</span>
                      <span className="legend-value">{cat.percentage}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="biz-no-data">No category data available</div>
            )}
          </div>

          {/* Income by Source Pie Chart */}
          <div className="biz-chart-card">
            <h3>Income by Source</h3>
            {profitLossData?.income_by_source?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={profitLossData.income_by_source}
                      dataKey="total_amount"
                      nameKey="source_label"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                    >
                      {profitLossData.income_by_source.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Source Legend */}
                <div className="biz-pie-legend">
                  {profitLossData.income_by_source.slice(0, 5).map((src, index) => (
                    <div key={src.source_type} className="legend-item">
                      <span className="legend-color" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                      <span className="legend-label">{src.source_icon} {src.source_label}</span>
                      <span className="legend-value">{src.percentage}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="biz-no-data">No income data available</div>
            )}
          </div>
        </div>

        {/* Top 5 Expenses */}
        {insightsData?.top_5_expenses?.length > 0 && (
          <div className="biz-top-expenses">
            <h3>Top 5 Expenses</h3>
            <div className="top-expenses-list">
              {insightsData.top_5_expenses.map((exp, index) => (
                <div key={exp.id} className="top-expense-row">
                  <span className="expense-rank">#{index + 1}</span>
                  <span className="expense-icon">{exp.category_icon}</span>
                  <div className="expense-info">
                    <span className="expense-desc">{exp.description}</span>
                    <span className="expense-meta">{exp.group_name} • {exp.type_name}</span>
                  </div>
                  <span className="expense-amount">€{formatNumber(exp.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ===============================================================================
  // Render Business Type Breakdown Tab
  // ===============================================================================
  const renderTypeBreakdownTab = () => (
    <div className="biz-tab-content">
      <div className="biz-section-header">
        <h3>Expenses by Business Type</h3>
        <p className="section-subtitle">Breakdown by business type → category</p>
      </div>

      {summaryData?.by_business_type?.length > 0 ? (
        <div className="biz-breakdown-list">
          {summaryData.by_business_type.map(type => (
            <div key={type.type_id} className="biz-type-block">
              <div 
                className="type-header"
                onClick={() => toggleType(type.type_id)}
              >
                <div className="type-info">
                  <span className="type-icon">{type.type_icon}</span>
                  <div className="type-details">
                    <span className="type-name">{type.type_name}</span>
                    <span className="type-count">{type.expense_count} expenses</span>
                  </div>
                </div>
                <div className="type-amount">
                  <span className="amount">€{formatNumber(type.total_amount)}</span>
                  <span className="expand-icon">{expandedTypes[type.type_id] ? '▼' : '▶'}</span>
                </div>
              </div>

              {expandedTypes[type.type_id] && (
                <div className="type-categories">
                  {type.categories.map(cat => (
                    <div key={`${type.type_id}-${cat.category_code}`} className="category-row">
                      <span className="cat-icon">{cat.category_icon}</span>
                      <span className="cat-name">
                        {cat.category_name}
                        {cat.is_custom && <span className="custom-badge">Custom</span>}
                      </span>
                      <span className="cat-count">{cat.expense_count}</span>
                      <span className="cat-amount">€{formatNumber(cat.total_amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="biz-empty-state">
          <span className="empty-icon">📊</span>
          <p>No expense data found for the selected period</p>
        </div>
      )}
    </div>
  );

  // ===============================================================================
  // Render Group Breakdown Tab
  // ===============================================================================
  const renderGroupBreakdownTab = () => (
    <div className="biz-tab-content">
      <div className="biz-section-header">
        <h3>Expenses by Group</h3>
        <p className="section-subtitle">Detailed breakdown per business group</p>
      </div>

      {summaryData?.groups?.length > 0 ? (
        <div className="biz-breakdown-list">
          {summaryData.groups.map(group => (
            <div key={group.group_id} className="biz-group-block">
              <div 
                className="group-header"
                onClick={() => toggleGroup(group.group_id)}
              >
                <div className="group-info">
                  <span className="group-type-icon">{group.type_icon}</span>
                  <div className="group-details">
                    <span className="group-name">{group.group_name}</span>
                    <span className="group-type">{group.type_name} • {group.currency}</span>
                  </div>
                </div>
                <div className="group-amount">
                  <div className="amount-info">
                    <span className="amount">€{formatNumber(group.total_amount)}</span>
                    <span className="count">{group.expense_count} expenses</span>
                  </div>
                  <span className="expand-icon">{expandedGroups[group.group_id] ? '▼' : '▶'}</span>
                </div>
              </div>

              {expandedGroups[group.group_id] && (
                <div className="group-categories">
                  {group.categories.map(cat => (
                    <div key={`${group.group_id}-${cat.category_code}`} className="category-block">
                      <div 
                        className="category-header"
                        onClick={() => toggleCategory(`${group.group_id}-${cat.category_id}`)}
                      >
                        <span className="cat-icon">{cat.category_icon}</span>
                        <span className="cat-name">
                          {cat.category_name}
                          {cat.is_custom && <span className="custom-badge">Custom</span>}
                        </span>
                        <span className="cat-count">{cat.expense_count}</span>
                        <span className="cat-amount">€{formatNumber(cat.total_amount)}</span>
                        <span className="expand-icon">
                          {expandedCategories[`${group.group_id}-${cat.category_id}`] ? '▼' : '▶'}
                        </span>
                      </div>

                      {expandedCategories[`${group.group_id}-${cat.category_id}`] && cat.members?.length > 0 && (
                        <div className="category-members">
                          <div className="members-header">
                            <span>Member</span>
                            <span>Paid</span>
                            <span>Owed</span>
                            <span>Net</span>
                          </div>
                          {cat.members.map(member => (
                            <div key={member.user_id} className="member-row">
                              <span className="member-name">{member.name}</span>
                              <span className="member-paid">€{formatNumber(member.amount_paid)}</span>
                              <span className="member-owed">€{formatNumber(member.amount_owed)}</span>
                              <span className={`member-net ${(member.amount_paid - member.amount_owed) >= 0 ? 'positive' : 'negative'}`}>
                                {(member.amount_paid - member.amount_owed) >= 0 ? '+' : ''}
                                €{formatNumber(member.amount_paid - member.amount_owed)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="biz-empty-state">
          <span className="empty-icon">🏢</span>
          <p>No group data found for the selected period</p>
        </div>
      )}
    </div>
  );

  // ===============================================================================
  // Render Per Person Tab - FIXED LAYOUT with Current Balance
  // ===============================================================================
  const renderPerPersonTab = () => (
    <div className="biz-tab-content">
      <div className="biz-section-header">
        <h3>Per Person Analysis</h3>
        <p className="section-subtitle">Individual contribution across business groups</p>
      </div>

      {perPersonData?.persons?.length > 0 ? (
        <div className="biz-persons-list">
          {perPersonData.persons.map(person => (
            <div key={person.user_id} className="person-block">
              <div 
                className="person-header"
                onClick={() => togglePerson(person.user_id)}
              >
                <div className="person-info">
                  <div className="person-avatar">
                    {person.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="person-details">
                    <span className="person-name">{person.name}</span>
                    <span className="person-username">@{person.username}</span>
                  </div>
                </div>
                <div className="person-stats">
                  <div className="person-stat">
                    <span className="stat-label">Paid</span>
                    <span className="stat-value positive">€{formatNumber(person.total_paid)}</span>
                  </div>
                  <div className="person-stat">
                    <span className="stat-label">Owed</span>
                    <span className="stat-value negative">€{formatNumber(person.total_owed)}</span>
                  </div>
                  <div className="person-stat">
                    <span className="stat-label">Period Net</span>
                    <span className={`stat-value ${person.net_balance >= 0 ? 'positive' : 'negative'}`}>
                      {person.net_balance >= 0 ? '+' : ''}€{formatNumber(person.net_balance)}
                    </span>
                  </div>
                  <div className="person-stat current-balance-stat">
                    <span className="stat-label">Current Balance</span>
                    <span className={`stat-value ${(person.current_balance || 0) >= 0 ? 'positive' : 'negative'}`}>
                      {(person.current_balance || 0) >= 0 ? '+' : ''}€{formatNumber(person.current_balance || 0)}
                    </span>
                  </div>
                  <span className="expand-icon">{expandedPersons[person.user_id] ? '▼' : '▶'}</span>
                </div>
              </div>

              {expandedPersons[person.user_id] && (
                <div className="person-groups">
                  <div className="person-groups-header">
                    <span>Group</span>
                    <span>Type</span>
                    <span>Paid</span>
                    <span>Owed</span>
                    <span>Period Net</span>
                    <span>Current Bal.</span>
                  </div>
                  {person.groups?.map(grp => (
                    <div key={grp.group_id} className="person-group-row">
                      <span className="group-name">{grp.group_name}</span>
                      <span className="group-type">{grp.type_name}</span>
                      <span className="group-paid">€{formatNumber(grp.total_paid)}</span>
                      <span className="group-owed">€{formatNumber(grp.total_owed)}</span>
                      <span className={`group-balance ${grp.net_balance >= 0 ? 'positive' : 'negative'}`}>
                        {grp.net_balance >= 0 ? '+' : ''}€{formatNumber(grp.net_balance)}
                      </span>
                      <span className={`group-current ${(grp.current_balance || 0) >= 0 ? 'positive' : 'negative'}`}>
                        {(grp.current_balance || 0) >= 0 ? '+' : ''}€{formatNumber(grp.current_balance || 0)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="biz-empty-state">
          <span className="empty-icon">👥</span>
          <p>No person data found for the selected period</p>
        </div>
      )}
    </div>
  );

  // ===============================================================================
  // Render Trends Tab
  // ===============================================================================
  const renderTrendsTab = () => (
    <div className="biz-tab-content">
      {/* Monthly Spending Chart */}
      <div className="biz-chart-card full-width">
        <h3>Monthly Business Spending</h3>
        {trendsData?.monthly_spending?.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={trendsData.monthly_spending}>
              <defs>
                <linearGradient id="bizBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2D5A3D" />
                  <stop offset="100%" stopColor="#4A7C59" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month_display" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => `€${formatNumber(v, 0)}`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="total_amount" 
                name="Total Spending" 
                fill="url(#bizBarGradient)" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="biz-no-data">No monthly data available</div>
        )}
      </div>

      {/* Category Breakdown Chart */}
      <div className="biz-chart-card full-width">
        <h3>Category Breakdown</h3>
        {trendsData?.category_totals?.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart 
                data={trendsData.category_totals.slice(0, 10)} 
                layout="vertical"
                margin={{ left: 8, right: 1 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => `€${formatNumber(v, 0)}`} />
                <YAxis 
                  type="category" 
                  dataKey="category_name" 
                  tick={{ fill: '#6b7280', fontSize: 11 }} 
                  width={110}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="total_amount" 
                  name="Total"
                  radius={[0, 4, 4, 0]}
                >
                  {trendsData.category_totals.slice(0, 10).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Category Stats Table */}
            <div className="biz-category-table">
              <div className="table-header">
                <span>Category</span>
                <span>Expenses</span>
                <span>Total</span>
                <span>Percentage</span>
              </div>
              {trendsData.category_totals.map((cat, index) => (
                <div key={cat.category_code} className="table-row">
                  <span className="cat-name">
                    <span className="cat-color" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                    {cat.category_icon} {cat.category_name}
                    {cat.is_custom && <span className="custom-badge">Custom</span>}
                  </span>
                  <span>{cat.expense_count}</span>
                  <span>€{formatNumber(cat.total_amount)}</span>
                  <span className="cat-percent">{cat.percentage}%</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="biz-no-data">No category data available</div>
        )}
      </div>

      {/* Business Type Breakdown */}
      <div className="biz-chart-card full-width">
        <h3>Business Type Breakdown</h3>
        {trendsData?.type_totals?.length > 0 ? (
          <div className="biz-type-table">
            <div className="table-header">
              <span>Business Type</span>
              <span>Groups</span>
              <span>Expenses</span>
              <span>Total</span>
              <span>Percentage</span>
            </div>
            {trendsData.type_totals.map((type, index) => (
              <div key={type.type_id} className="table-row">
                <span className="type-name">
                  <span className="type-color" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                  {type.type_icon} {type.type_name}
                </span>
                <span>{type.group_count}</span>
                <span>{type.expense_count}</span>
                <span>€{formatNumber(type.total_amount)}</span>
                <span className="type-percent">{type.percentage}%</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="biz-no-data">No business type data available</div>
        )}
      </div>
    </div>
  );

  // ===============================================================================
  // Render Profit/Loss Tab
  // ===============================================================================
  const renderProfitLossTab = () => (
    <div className="biz-tab-content">
      {/* Profit/Loss Summary Cards */}
      <div className="biz-pl-summary-cards">
        <div className="biz-pl-card income">
          <div className="pl-card-icon">📥</div>
          <div className="pl-card-content">
            <span className="pl-card-label">Total Income</span>
            <span className="pl-card-value">€{formatNumber(profitLossData?.total_income)}</span>
          </div>
        </div>
        <div className="biz-pl-card expenses">
          <div className="pl-card-icon">📤</div>
          <div className="pl-card-content">
            <span className="pl-card-label">Total Expenses</span>
            <span className="pl-card-value">€{formatNumber(profitLossData?.total_expenses)}</span>
          </div>
        </div>
        <div className={`biz-pl-card net ${(profitLossData?.net_profit_loss || 0) >= 0 ? 'profit' : 'loss'}`}>
          <div className="pl-card-icon">{(profitLossData?.net_profit_loss || 0) >= 0 ? '📈' : '📉'}</div>
          <div className="pl-card-content">
            <span className="pl-card-label">Net {(profitLossData?.net_profit_loss || 0) >= 0 ? 'Profit' : 'Loss'}</span>
            <span className="pl-card-value">
              {(profitLossData?.net_profit_loss || 0) >= 0 ? '+' : ''}€{formatNumber(profitLossData?.net_profit_loss)}
            </span>
          </div>
        </div>
      </div>

      {/* Groups Performance Summary */}
      <div className="biz-pl-performance">
        <div className="performance-item profit">
          <span className="perf-count">{profitLossData?.groups_with_profit || 0}</span>
          <span className="perf-label">Groups with Profit</span>
        </div>
        <div className="performance-item loss">
          <span className="perf-count">{profitLossData?.groups_with_loss || 0}</span>
          <span className="perf-label">Groups with Loss</span>
        </div>
      </div>

      {/* Monthly Profit/Loss Chart */}
      <div className="biz-chart-card full-width">
        <h3>Monthly Profit/Loss Trend</h3>
        {profitLossData?.monthly_profit_loss?.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={profitLossData.monthly_profit_loss}>
              <defs>
                <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PROFIT_COLOR} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={PROFIT_COLOR} stopOpacity={0.3} />
                </linearGradient>
                <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={LOSS_COLOR} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={LOSS_COLOR} stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month_display" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => `€${formatNumber(v, 0)}`} />
              <Tooltip content={<ProfitLossTooltip />} />
              <Legend />
              <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
              <Bar dataKey="total_income" name="Income" fill={PROFIT_COLOR} radius={[4, 4, 0, 0]} />
              <Bar dataKey="total_expenses" name="Expenses" fill={LOSS_COLOR} radius={[4, 4, 0, 0]} />
              <Line 
                type="monotone" 
                dataKey="net_profit_loss" 
                name="Net P/L" 
                stroke="#D4A574" 
                strokeWidth={3}
                dot={{ fill: '#D4A574', strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="biz-no-data">No monthly data available</div>
        )}
      </div>

      {/* Income by Source Breakdown */}
      <div className="biz-chart-card full-width">
        <h3>Income by Source</h3>
        {profitLossData?.income_by_source?.length > 0 ? (
          <div className="biz-source-table">
            <div className="table-header">
              <span>Source</span>
              <span>Entries</span>
              <span>Total</span>
              <span>Percentage</span>
            </div>
            {profitLossData.income_by_source.map((src, index) => (
              <div key={src.source_type} className="table-row">
                <span className="source-name">
                  <span className="source-color" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                  {src.source_icon} {src.source_label}
                </span>
                <span>{src.income_count}</span>
                <span className="income-amount">€{formatNumber(src.total_amount)}</span>
                <span className="source-percent">{src.percentage}%</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="biz-no-data">No income data available</div>
        )}
      </div>

      {/* Group Profit/Loss Breakdown */}
      <div className="biz-section-header">
        <h3>Profit/Loss by Group</h3>
        <p className="section-subtitle">Detailed breakdown per business group</p>
      </div>

      {profitLossData?.groups?.length > 0 ? (
        <div className="biz-pl-groups-list">
          {profitLossData.groups.map(group => (
            <div key={group.group_id} className={`biz-pl-group-block ${group.net_profit_loss >= 0 ? 'profit' : 'loss'}`}>
              <div 
                className="pl-group-header"
                onClick={() => togglePLGroup(group.group_id)}
              >
                <div className="pl-group-info">
                  <span className="pl-group-icon">{group.type_icon}</span>
                  <div className="pl-group-details">
                    <span className="pl-group-name">{group.group_name}</span>
                    <span className="pl-group-type">{group.type_name} • {group.currency}</span>
                  </div>
                </div>
                <div className="pl-group-summary">
                  <div className="pl-mini-stat income">
                    <span className="mini-label">Income</span>
                    <span className="mini-value">€{formatNumber(group.total_income)}</span>
                  </div>
                  <div className="pl-mini-stat expense">
                    <span className="mini-label">Expenses</span>
                    <span className="mini-value">€{formatNumber(group.total_expenses)}</span>
                  </div>
                  <div className={`pl-mini-stat net ${group.net_profit_loss >= 0 ? 'profit' : 'loss'}`}>
                    <span className="mini-label">Net</span>
                    <span className="mini-value">
                      {group.net_profit_loss >= 0 ? '+' : ''}€{formatNumber(group.net_profit_loss)}
                    </span>
                  </div>
                  <span className="expand-icon">{expandedPLGroups[group.group_id] ? '▼' : '▶'}</span>
                </div>
              </div>

              {expandedPLGroups[group.group_id] && (
                <div className="pl-group-details-expanded">
                  <div className="pl-details-grid">
                    <div className="pl-detail-item">
                      <span className="detail-label">Income Entries</span>
                      <span className="detail-value">{group.income_count}</span>
                    </div>
                    <div className="pl-detail-item">
                      <span className="detail-label">Expense Entries</span>
                      <span className="detail-value">{group.expense_count}</span>
                    </div>
                    <div className="pl-detail-item">
                      <span className="detail-label">Profit Margin</span>
                      <span className={`detail-value ${group.profit_margin >= 0 ? 'positive' : 'negative'}`}>
                        {formatNumber(group.profit_margin, 1)}%
                      </span>
                    </div>
                  </div>
                  <div className="pl-your-share">
                    <h4>Your Share</h4>
                    <div className="your-share-row">
                      <span className="share-item">
                        <span className="share-label">Your Income:</span>
                        <span className="share-value income">€{formatNumber(group.your_income_share)}</span>
                      </span>
                      <span className="share-item">
                        <span className="share-label">Your Expenses:</span>
                        <span className="share-value expense">€{formatNumber(group.your_expense_share)}</span>
                      </span>
                      <span className="share-item">
                        <span className="share-label">Your Net:</span>
                        <span className={`share-value ${group.your_net >= 0 ? 'profit' : 'loss'}`}>
                          {group.your_net >= 0 ? '+' : ''}€{formatNumber(group.your_net)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="biz-empty-state">
          <span className="empty-icon">💰</span>
          <p>No profit/loss data found for the selected period</p>
        </div>
      )}
    </div>
  );

  // ===============================================================================
  // Main Render
  // ===============================================================================
  return (
    <div className="biz-analysis-page">
      {/* Header */}
      <div className="biz-analysis-header">
        <div className="biz-header-content">
          <div className="biz-header-title">
            <h1>📊 Business Analysis</h1>
            <span className="biz-premium-badge">⭐ Premium</span>
          </div>
          <p className="biz-header-subtitle">
            Comprehensive insights into your business income & spending
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="biz-filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label>Quick Range</label>
            <select
              value={dateRangePreset}
              onChange={handlePresetChange}
              className="biz-date-input"
            >
              {DATE_RANGE_PRESETS.map(p => (
                <option key={p.id} value={p.id} hidden={p.id === 'custom'}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>From</label>
            <input 
              type="date" 
              value={dateFrom} 
              onChange={(e) => handleManualDateChange('from', e.target.value)}
              className="biz-date-input"
            />
          </div>
          <div className="filter-group">
            <label>To</label>
            <input 
              type="date" 
              value={dateTo} 
              onChange={(e) => handleManualDateChange('to', e.target.value)}
              className="biz-date-input"
            />
          </div>
          <div className="filter-group">
            <label>Groups</label>
            <button 
              className="biz-group-filter-btn"
              onClick={() => setShowGroupFilter(!showGroupFilter)}
            >
              {selectedGroupIds.length === 0 
                ? 'All Groups' 
                : `${selectedGroupIds.length} Selected`}
              <span className="dropdown-icon">▼</span>
            </button>
          </div>
          <button className="biz-btn-primary" onClick={handleApplyFilters}>
            Apply Filters
          </button>
        </div>

        {/* Group Filter Dropdown */}
        {showGroupFilter && (
          <div className="biz-group-filter-dropdown">
            <div className="dropdown-header">
              <span>Select Business Groups</span>
              <div className="dropdown-actions">
                <button onClick={selectAllGroups}>Select All</button>
                <button onClick={clearAllGroups}>Clear All</button>
              </div>
            </div>
            <div className="dropdown-list">
              {availableGroups.map(group => (
                <label key={group.id} className="group-option">
                  <input 
                    type="checkbox"
                    checked={selectedGroupIds.includes(group.id)}
                    onChange={() => toggleGroupSelection(group.id)}
                  />
                  <span className="group-icon">{group.type_icon}</span>
                  <span className="group-name">{group.name}</span>
                  <span className="group-type">{group.type_name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <p className="date-range-display">
          Showing data from {formatDateDisplay(dateFrom)} to {formatDateDisplay(dateTo)}
          {selectedGroupIds.length > 0 && ` • ${selectedGroupIds.length} group(s) selected`}
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="biz-error-banner">
          <span>{error}</span>
          <button onClick={() => setError("")}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="biz-tabs-container">
        <div className="biz-tabs">
          <button 
            className={`biz-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            <span className="tab-icon">📈</span>
            <span className="tab-text">Overview</span>
          </button>
          <button 
            className={`biz-tab ${activeTab === "trends" ? "active" : ""}`}
            onClick={() => setActiveTab("trends")}
          >
            <span className="tab-icon">📊</span>
            <span className="tab-text">Trends</span>
          </button>
          <button 
            className={`biz-tab ${activeTab === "profit-loss" ? "active" : ""}`}
            onClick={() => setActiveTab("profit-loss")}
          >
            <span className="tab-icon">💰</span>
            <span className="tab-text">Profit/Loss</span>
          </button>
          <button 
            className={`biz-tab ${activeTab === "by-type" ? "active" : ""}`}
            onClick={() => setActiveTab("by-type")}
          >
            <span className="tab-icon">🏢</span>
            <span className="tab-text">By Type</span>
          </button>
          <button 
            className={`biz-tab ${activeTab === "by-group" ? "active" : ""}`}
            onClick={() => setActiveTab("by-group")}
          >
            <span className="tab-icon">📁</span>
            <span className="tab-text">By Group</span>
          </button>
          <button 
            className={`biz-tab ${activeTab === "per-person" ? "active" : ""}`}
            onClick={() => setActiveTab("per-person")}
          >
            <span className="tab-icon">👥</span>
            <span className="tab-text">Per Person</span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && renderOverviewTab()}
      {activeTab === "profit-loss" && renderProfitLossTab()}
      {activeTab === "by-type" && renderTypeBreakdownTab()}
      {activeTab === "by-group" && renderGroupBreakdownTab()}
      {activeTab === "per-person" && renderPerPersonTab()}
      {activeTab === "trends" && renderTrendsTab()}
    </div>
  );
}

export default BusinessExpenseAnalysis;

